import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import PendingActionsWidget from '../../components/PendingActionsWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Users, DollarSign, FileCheck, TrendingUp, AlertCircle, Clock
} from 'lucide-react';
import axios from 'axios';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [statsRes, auditRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/audit?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setRecentActivity(auditRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Chart data from real stats
  const [chartData, setChartData] = useState([]);

  const barData = [
    { name: 'Dépôts', value: stats?.pending_deposits || 0 },
    { name: 'Retraits', value: stats?.pending_withdrawals || 0 },
    { name: 'KYC', value: stats?.pending_kyc || 0 },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <PendingActionsWidget onNavigateClient={(id) => navigate(`/admin/clients/${id}`)} />
        {/* Welcome Section */}
        <div className="glass rounded-sm p-6">
          <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('welcome')}, {user?.first_name}
          </h2>
          <p className="text-muted-foreground">
            Voici un aperçu de l'activité de la plateforme
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="card-premium cursor-pointer hover:ring-1 hover:ring-primary/30 transition-all" data-testid="stat-total-clients" onClick={() => navigate('/admin/clients')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('total_clients')}</p>
                  <p className="text-3xl font-bold mt-1">{stats?.total_clients || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#D4AF37]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-premium cursor-pointer hover:ring-1 hover:ring-primary/30 transition-all" data-testid="stat-total-aum" onClick={() => navigate('/admin/clients')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('total_aum')}</p>
                  <p className="text-3xl font-bold mt-1">
                    €{(stats?.total_aum || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-premium cursor-pointer hover:ring-1 hover:ring-primary/30 transition-all" data-testid="stat-pending-deposits" onClick={() => navigate('/admin/transactions?status=pending&tx_type=depot')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('pending_deposits')}</p>
                  <p className="text-3xl font-bold mt-1">{stats?.pending_deposits || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-premium cursor-pointer hover:ring-1 hover:ring-primary/30 transition-all" data-testid="stat-pending-withdrawals" onClick={() => navigate('/admin/withdrawals')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('pending_withdrawals')}</p>
                  <p className="text-3xl font-bold mt-1">{stats?.pending_withdrawals || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="text-lg">Évolution des actifs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center">
                {stats?.total_aum > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[{name: 'Actuel', value: stats?.total_aum || 0}]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="name" stroke="rgba(255,255,255,0.5)" />
                    <YAxis stroke="rgba(255,255,255,0.5)" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))' 
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#D4AF37" 
                      strokeWidth={2}
                      dot={{ fill: '#D4AF37' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-sm">Aucune donnée à afficher</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="text-lg">Demandes en attente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="name" stroke="rgba(255,255,255,0.5)" />
                    <YAxis stroke="rgba(255,255,255,0.5)" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))' 
                      }}
                    />
                    <Bar dataKey="value" fill="#D4AF37" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="card-premium">
          <CardHeader>
            <CardTitle className="text-lg">Activité récente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">{t('no_data')}</p>
              ) : (
                recentActivity.map((activity, index) => (
                  <div 
                    key={activity.id || index}
                    className="flex items-center gap-4 p-3 rounded-sm bg-white/5"
                  >
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {activity.action?.replace('_', ' ')}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {activity.details}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(activity.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
